import { animate, state, style, transition, trigger } from '@angular/animations';
import { Component, EventEmitter, HostListener, Inject, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { Router, NavigationEnd } from '@angular/router';
import { OktaAuthStateService, OKTA_AUTH } from '@okta/okta-angular';
import { Subject } from 'rxjs';
import { combineLatest } from 'rxjs';
import { ContractGuard } from 'src/app/core/guards/contract.guard';
import { filter, takeUntil } from 'rxjs/operators';
import { ISelectedContract, LoggedInUserSettings } from 'src/app/core/services/settings/logged-in-user-settings';
import { IImageProperties, ILogo } from 'src/app/model/sitefinity/IGlobalHeaders';
import { myAccountRouteWithSlash, myTechServicesRouteWithSlash } from '../../../route-constants';
import { CLAIMSANDSUPPORTURL, CLAIMSSUPPORTURLNAME, LANGUAGES, PGIDPERKURLNAME, PROFILEURLNAME, PROGRAMINFORMATION, VIEWCLAIMSTATUS, TECHSERVICEURLNAME, COVERAGEURLNAME, VIEWTUTORIALS } from '../../constants/feature-key.constants';
import { Location } from '@angular/common';
import { PGHOktaService } from '../../services/pghokta.service';
import { GSSPAuthenticationService } from 'src/app/core/services/gssp-authentication.service';
import { ClaimsNoDevicesModalWindowComponent } from 'src/app/shared/components/claims-no-devices-modal-window/claims-no-devices-modal-window.component';
import { DomSanitizer } from '@angular/platform-browser';
import { SecurityContext } from '@angular/core';
import { PghUserDeviceBase } from 'src/app/shared/device/pgh-user-device';
import { SelectedDeviceService } from 'src/app/shared/device/selected-device.service';
import { ADPResourceService, SitefinityMenuItem } from 'digital-core';
import { SessionManagementService } from '../../services/session-management.service';
import { GlobalModalWindowService } from '../../services/global-modal-window.service';
import { PGHAppLoadService } from '../../services/pgh-app-load.service';
import { OktaAuth } from '@okta/okta-auth-js'
import { StorageService } from '../../services/storage.service';
import { GetStartedModalComponent } from 'src/app/shared/components/get-started-modal/get-started-modal.component';

@Component({
    selector: 'app-header',
    animations: [
        trigger('openClose', [
            state('open', style({
                transformOrigin: 'center center',
                transform: 'rotate(0deg)'
            })),
            state('closed', style({
                transformOrigin: 'center center',
                transform: 'rotate(-180deg)'
            })),
            transition('open => closed', [
                animate('0.3s')
            ]),
            transition('closed => open', [
                animate('0.3s')
            ]),
        ]),
    ],
    templateUrl: './header.component.html',
    styleUrls: ['./header.component.scss']
})
export class HeaderComponent implements OnInit, OnChanges, OnDestroy {
    @Input() isSidenavShowing: boolean;
    @Output() onMenuShow: EventEmitter<boolean> = new EventEmitter<boolean>();
    readonly OKTA_TOKEN_STORAGE = 'okta-token-storage';

    isOpen = false;
    isProfileMenuOpen = false;
    isClaimsMenuOpen = false;
    isCoverageMenuOpen = false;
    isProgramMenuOpen = false;
    isTechServicesInMenu = false;
    areLinksLoaded: boolean = false;
    areClaimsLinksLoaded: boolean = false;
    areCoverageLinksLoaded: boolean = false;
    isAuthenticated: boolean;
    headerLoggedInSettings: SitefinityMenuItem[] = null;
    headerLoggedOutSettings: SitefinityMenuItem[] = null;
    tempHeaderSettings: SitefinityMenuItem[] = null;
    tempProfileLinks: SitefinityMenuItem[] = null;
    tempClaimLinks: SitefinityMenuItem[] = null;
    tempCoverageLinks: SitefinityMenuItem[] = null;
    tempProgramInfoLinks: SitefinityMenuItem[] = null;
    profileLinks: SitefinityMenuItem[];
    defaultProfileLinks: SitefinityMenuItem[];
    claimsLinks: SitefinityMenuItem[];
    defaultClaimsLinks: SitefinityMenuItem[];
    coverageLinks: SitefinityMenuItem[];
    defaultCoverageLinks: SitefinityMenuItem[];
    programInfoLinks: SitefinityMenuItem[];
    logoutHeaderHasLanguage = false;
    userGivenName: string = '';
    userAvatarUrl: string = '';
    userAvatarColor: string = '';
    isExpired: boolean = false;
    isCancelled: boolean = false;
    cancelledOrExpiredHasFullAccess: boolean = false;
    isPTPO: boolean = false;
    hasActiveAccount: boolean = true;
    headerLogos: ILogo[];
    alesSiteDetails: IImageProperties;
    isExternalOrigin: boolean = false;
    isAIZHeader: boolean = false;
    isPGHEssentialHeader: boolean = false;
    isTechServicesEnabled: boolean = false;
    isGSSP = false;
    dealerCode: string;
    masterCertificateNumber: string;
    coverageTypeCode: string;
    redirectUrl: string;
    contractNum: string;
    zipCode: string;
    isActive: boolean;
    isMainNavVisible: boolean = true;
    allDevices: PghUserDeviceBase[];
    originQueryParam: any;
    childCertificateNumber: string;
    isSRLoaded = false;
    userFullName: string = '';
    isSessionServiceInvoked = false;
    languages: any;
    selectedLanguageKey: string = null;
    chooseLanguage: string = null;
    chooseLanguageTitle: string = null;
    private destroy$ = new Subject<boolean>();

    constructor(
        @Inject(OKTA_AUTH) public oktaAuth: OktaAuth,
        private oktaAuthservice: OktaAuthStateService,
        private selectedDeviceService: SelectedDeviceService,
        private appLoadService: PGHAppLoadService,
        private userSettings: LoggedInUserSettings,
        private matDialog: MatDialog,
        private contractGuard: ContractGuard,
        private router: Router,
        private location: Location,
        private domSanitizer: DomSanitizer,
        private gsspAuthService: GSSPAuthenticationService,
        private pghOktaService: PGHOktaService,
        public resourceService: ADPResourceService,
        private sessionManagementService: SessionManagementService,
        private globalModalWindowService: GlobalModalWindowService,
        private storageService: StorageService
    ) { }

    async ngOnInit(): Promise<void> {
        this.appLoadService.isSiteResourcesLoaded.subscribe(value => {
            this.isSRLoaded = value;
            if (value) {
                this.getLanguages();
                this.setAppHeaders();
            }
        });
        this.isAuthenticated = this.pghOktaService.isTokenValid();
        let userClaims = null;

        combineLatest([
            this.router.events.pipe(filter(route => route instanceof NavigationEnd))
        ]).subscribe(([routerEvents]: [any]) => {
            // if phones onboarding or diagnostic routing, hide the main nav in header
            if (routerEvents.url.includes('/po') || routerEvents.url.includes('/pd') || routerEvents.url.includes('isOnboardingFlow')) {
                this.isMainNavVisible = false;
            } else {
                this.isMainNavVisible = true;
            }
            this.handleActiveRoutes(routerEvents.url);
        });

        this.oktaAuthservice.authState$
            .pipe(takeUntil(this.destroy$))
            .subscribe(async (authState) => {
                this.isAuthenticated = authState.isAuthenticated;
                if (this.isAuthenticated) {
                    this.appLoadService.isSiteResourcesLoaded.subscribe(value => {
                        if (value && !this.isSessionServiceInvoked) {
                            this.sessionManagementService.configureSessionTimeOut();
                            this.isSessionServiceInvoked = true;
                        }
                    });
                    userClaims = await this.pghOktaService.getUser();
                    if (!userClaims) {
                        await this.clearSession();
                    } else {
                        this.userGivenName = userClaims.given_name;
                        this.userFullName = userClaims.name;
                        if (this.userGivenName) {
                            this.userGivenName = this.userGivenName[0].toUpperCase() + this.userGivenName.substr(1).toLowerCase();
                        }
                    }
                } else {
                    // await this.clearSession();
                }
            });

        if (this.isAuthenticated) {
            userClaims = await this.pghOktaService.getUser();
            this.userGivenName = userClaims.given_name;
            this.userFullName = userClaims.name;
            if (this.userGivenName) {
                this.userGivenName = this.userGivenName[0].toUpperCase() + this.userGivenName.substr(1).toLowerCase();
            }
        }

        this.userSettings.$selectedContract
            .pipe(takeUntil(this.destroy$))
            .subscribe((contract) => {
                if (!userClaims) {
                    return;
                }
                if (contract) {
                    const isEprismOnly = this.storageService.getLocal<string>(`IsEprismOnly`);
                    const loggedInMember = isEprismOnly == 'true' ?  contract?.members[0]
                        : contract?.members?.find(m => m?.emailAddress?.toLowerCase() === userClaims?.email?.toLowerCase());
                    this.isExpired = contract.isExpired;
                    this.isCancelled = contract.isCancelled;
                    this.isPTPO = contract.isPTPO;
                    if (loggedInMember) {
                        this.userAvatarUrl = loggedInMember.avatarUrl;
                        this.userAvatarColor = loggedInMember.profileColor;
                    }
                }
            });

        this.userSettings.$hasActiveAccount
            .pipe(takeUntil(this.destroy$))
            .subscribe((hasActiveAccount) => {
                this.hasActiveAccount = hasActiveAccount;
            });
    }

    private handleActiveRoutes(routeUrl) {
        const isSPOEEnabled = this.resourceService.isPortalSettingTrue("Global_SPOE_Enabled");
        if ((routeUrl.includes('/device-') || routeUrl.includes('/home-device')) && routeUrl != '/device-select') {
            //this.renderer.addClass(routerEvents.id, "active-route");
            //this.element(document.querySelector(routerEvents.id)).addClass('.active-route');
            const alink = document.getElementById('my-devices');
            if (alink != null) {
              setTimeout(() => {
                const alink = document.getElementById('my-devices');
                alink.classList.add('active');
              }, 10);
            }
            const supportlink = document.getElementById('claimsSupportMenu');
            if (supportlink != null) {
                supportlink.classList.remove('active-route');
            }
        } else if (routeUrl === '/device-select' || routeUrl === '/claims-support') {
            const alink = document.getElementById('claimsSupportMenu');
            if (alink != null && !isSPOEEnabled) {
              setTimeout(() => {
                const alink = document.getElementById('claimsSupportMenu');
                alink.classList.add('active');
              }, 500);
            }
            const devicelink = document.getElementById('my-devices');
            if (devicelink != null) {
                devicelink.classList.remove('active-route');
            }
        }
        else if (isSPOEEnabled && (routeUrl === '/tech-services' || routeUrl === '/speed-check')) {
          const alink = document.getElementById('claimsSupportMenu');
            if (alink != null) {
              setTimeout(() => {
                const alink = document.getElementById('claimsSupportMenu');
                alink.classList.add('active');
              }, 500);
            }
        }
        else if (isSPOEEnabled && (routeUrl === '/coverage-home' || routeUrl === '/coverage-mobile')) {
          const alink = document.getElementById('coverageMenu');
            if (alink != null) {
              setTimeout(() => {
                const alink = document.getElementById('coverageMenu');
                alink.classList.add('active');
              }, 500);
            }
        }
        else {
            /* CLEANUP */
            const alink = document.getElementById('my-devices');
            if (alink != null) {
                alink.classList.remove('active-route');
            }
            const supportlink = document.getElementById('claimsSupportMenu');
            if (supportlink != null) {
                supportlink.classList.remove('active-route');
            }
        }
    }

    private menuId(route) {
        const navId = route.replace('/', '');
        return navId;
    }

    private getLanguages() {
        let local = this.storageService.getLocal<string>(`language`);
        this.selectedLanguageKey = local ?? 'EN';
        let languages = this.resourceService.getListItemResource("Global_Languages");
        languages.subscribe((data) => {
            this.languages = data;
            const language = this.languages.find(x => x.Key.toLocaleLowerCase() === this.getSelectedLanguage().toLocaleLowerCase());
            this.selectedLanguageKey = language?.Key;
            this.chooseLanguage = this.languages.find(x => x.Key != this.selectedLanguageKey)?.Value;
            this.chooseLanguageTitle = this.resourceService.getViewLabel('Global_Language_Title') + this.chooseLanguage;
            if (!this.selectedLanguageKey) {
                this.storageService.setLocal(`language`, null);
                window.location.reload();
            }
        });
    }

    private setAppHeaders() {
        this.resourceService.getPortalMenus('Header_LoggedOut_Nav_Menu').subscribe(data => {
            this.headerLoggedOutSettings = data.MenuItems;
            if (this.headerLoggedOutSettings?.length) {
                const programLinks = this.headerLoggedOutSettings.find(x => x.LinkUrl === PROGRAMINFORMATION);
                if (programLinks) {
                    this.resourceService.getPortalMenus('Header_Program_Information_Child_Menu').subscribe(childData => {
                        this.programInfoLinks = JSON.parse(JSON.stringify(childData.MenuItems));
                    });
                }
                this.logoutHeaderHasLanguage = !!this.headerLoggedOutSettings.find(x => x.LinkUrl == LANGUAGES);
            }
        });
        this.resourceService.getPortalMenus('Header_LoggedIn_Nav_Menu').subscribe(data => {
            this.headerLoggedInSettings = data.MenuItems;
            if (!this.tempHeaderSettings) {
                this.tempHeaderSettings = JSON.parse(JSON.stringify(data.MenuItems));
            }
            if (this.headerLoggedInSettings?.length) {
                const profileLinks = this.headerLoggedInSettings.find(x => x.LinkUrl === PROFILEURLNAME);
                if (profileLinks) {
                    this.resourceService.getPortalMenus('Header_Profile_Child_Menu').subscribe(childData => {
                        this.defaultProfileLinks = childData.MenuItems;
                        if (!this.tempProfileLinks) {
                            this.tempProfileLinks = JSON.parse(JSON.stringify(childData.MenuItems));
                        }
                    });
                }
                const supportCenter = this.headerLoggedInSettings.find(x => x.LinkUrl.endsWith(CLAIMSSUPPORTURLNAME));
                if (supportCenter) {
                    this.resourceService.getPortalMenus('Header_Claims_Support_Child_Menu').subscribe(childData => {
                        this.defaultClaimsLinks = childData.MenuItems;
                        if (!this.tempClaimLinks) {
                            this.tempClaimLinks = JSON.parse(JSON.stringify(childData.MenuItems));
                        }
                    });
                }
                const techServicesMenu = this.headerLoggedInSettings.find(x => x.LinkUrl.endsWith(TECHSERVICEURLNAME));
                if (techServicesMenu) {
                    this.isTechServicesInMenu = true;
                }
            }
        });
        this.userSettings.$selectedContract.subscribe(contract => {
            let hasP360MobilePolicy = this.storageService.getLocal<string>(`EprismContractsCount`) > '0';
            let hasP360HomeTechPolicy = this.storageService.getLocal<string>(`ElitaContractsCount`) > '0';
            if (this.tempHeaderSettings?.length) {
                this.headerLoggedInSettings = JSON.parse(JSON.stringify(this.tempHeaderSettings));
                if (!contract?.isPrimary) {
                    this.headerLoggedInSettings = this.headerLoggedInSettings?.filter(x => x.LinkUrl !== '/claims');
                    this.headerLoggedInSettings = this.headerLoggedInSettings?.filter(x => x.LinkUrl !== '/file-a-claim');
                }
            }
            if (this.headerLoggedInSettings) {
                const profileLinks = this.headerLoggedInSettings?.find(x => x.LinkUrl === PROFILEURLNAME);
                if (profileLinks && this.tempProfileLinks?.length) {
                    this.profileLinks = (hasP360MobilePolicy && !hasP360HomeTechPolicy) || !contract?.isPrimary ?
                        this.tempProfileLinks.filter(x => !x.LinkUrl.includes('communication-preferences') && !x.LinkUrl.includes('family-management')) :
                        this.tempProfileLinks;
                    /*this.profileLinks = (hasP360MobilePolicy && !hasP360HomeTechPolicy && !contract?.isPrimary) ?
                        this.profileLinks.filter(x => !x.LinkUrl.includes('details')) :
                        this.profileLinks;*/
                    this.areLinksLoaded = true;
                }
                const supportCenter = this.headerLoggedInSettings.find(x => x.LinkUrl.endsWith(CLAIMSSUPPORTURLNAME));
                if (supportCenter && this.tempClaimLinks?.length) {
                    this.claimsLinks = this.tempClaimLinks;
                    this.claimsLinks = (hasP360MobilePolicy && !hasP360HomeTechPolicy) ?
                        this.claimsLinks.filter(x => !x.LinkUrl.includes('/faq')) :
                        this.claimsLinks;
                    this.claimsLinks = (!hasP360MobilePolicy && hasP360HomeTechPolicy) ?
                        this.claimsLinks.filter(x => !x.LinkUrl.includes('/p360')) :
                        this.claimsLinks;
                    this.areClaimsLinksLoaded = true;
                }

                const coverageLinks = this.headerLoggedInSettings.find(x => x.LinkUrl.endsWith(COVERAGEURLNAME));
                if (coverageLinks && contract) {
                    this.resourceService.getPortalMenus('Header_Coverage_Child_Menu').subscribe(childData => {
                        let filteredChildMenuItems = childData.MenuItems;

                        if (hasP360MobilePolicy && hasP360HomeTechPolicy) {
                            // Multipe policy both P360 Mobile & Home tech
                            filteredChildMenuItems = childData.MenuItems;
                        } else if (hasP360HomeTechPolicy) {
                            filteredChildMenuItems = childData.MenuItems.filter(x => x.LinkUrl.endsWith('home'));
                        } else if (hasP360MobilePolicy) {
                            filteredChildMenuItems = childData.MenuItems.filter(x => x.LinkUrl.endsWith('mobile'));
                        }

                        this.defaultCoverageLinks = filteredChildMenuItems;

                        if (!this.tempCoverageLinks) {
                            this.tempCoverageLinks = JSON.parse(JSON.stringify(filteredChildMenuItems));
                        }
                        if (this.tempCoverageLinks?.length) {
                            this.coverageLinks = this.tempCoverageLinks;
                            this.areCoverageLinksLoaded = true;
                        }
                    });
                }
            }
        });
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes.isSidenavShowing) {
            this.isOpen = this.isSidenavShowing;
        }
    }

    ngOnDestroy() {
        this.destroy$.next(true);
    }

    onToggleMenu(): void {
        this.isOpen = !this.isOpen;
        this.onMenuShow.emit(this.isOpen);
    }

    @HostListener('window:resize', ['$event'])
    onResize(event) {
        // when large breakpoint is hit and sidenav is showing
        if (event.target.innerWidth >= 992 && this.isSidenavShowing) {
            // hide sidenav
            this.onToggleMenu();
        }
    }

    checkRouteAvailability(route: string) {
        const cancelledOrExpiredHasFullAccess = this.resourceService.isPortalSettingTrue('Global_CancelledOrExpiredContract_HasFullAccess');
        if (((this.isCancelled || this.isExpired) && !cancelledOrExpiredHasFullAccess) && route === myAccountRouteWithSlash) {
            return false;
        } else if ((((this.isCancelled || this.isExpired) && !cancelledOrExpiredHasFullAccess) || this.isPTPO) && route === myTechServicesRouteWithSlash) {
            return false;
        } else {
            return true;
        }
    }

    profileButtonClick(navigationItem: SitefinityMenuItem) {
        let url = navigationItem.LinkUrl;
        if (navigationItem?.LinkUrl?.startsWith('/')) {
            this.router.navigate([url]);
        } else if (url.startsWith('http') || navigationItem?.LinkUrl?.endsWith(PGIDPERKURLNAME)) {
            window.open(url, '_blank');
        }
    }

    openTutorial(label: string) {
        const dialogConfig = new MatDialogConfig();
        dialogConfig.disableClose = false;
        dialogConfig.ariaLabel = label;
        dialogConfig.id = 'get-started-dialog';
        dialogConfig.height = 'auto';
        dialogConfig.data = {
          title: 'Account',
          content: '',
          buttonText: 'Next'
        };

        this.matDialog.open(GetStartedModalComponent, dialogConfig);
      }

    claimsButtonClick(navigationItem: SitefinityMenuItem) {
        if (navigationItem?.LinkUrl == VIEWCLAIMSTATUS) {
            this.viewClaimHistoryAndStatus();
            return;
        }

        if (navigationItem?.LinkUrl == VIEWTUTORIALS) {
            this.openTutorial(this.resourceService.getLabel('Perks_Tutorial_Button_Button'));
            return;
        }

        //special behavior for claims & support navigation link to only allow navigation to Claims & Support page if user
        //has devices in their inventory
        if (navigationItem?.LinkUrl == CLAIMSANDSUPPORTURL) {
            if (this.resourceService.getViewLabel('Global_Modal_NoDevices_Title')) {
                this.allDevices = this.selectedDeviceService.getAllDevices();
                if (this.allDevices?.length > 0) {
                    this.router.navigate([navigationItem?.LinkUrl]);
                }
                //display a modal notification to user that they have no devices in their inventory with an option to navigate to My Devices page
                else {
                    this.displayClaimsNoDevicesModal();
                }
            } else {
                this.router.navigate([navigationItem?.LinkUrl]);
            }
        } else {
            if (navigationItem?.LinkUrl.startsWith('http')) {
                window.open(navigationItem?.LinkUrl, '_blank');
            } else {
                this.router.navigate([navigationItem?.LinkUrl]);
            }
        }
    }

    displayClaimsNoDevicesModal(msg: string = '') {
        const dialogConfig = new MatDialogConfig();
        dialogConfig.ariaLabel = this.resourceService.getPortalLabel("Global_Modal_NoDevices_Title").Value;
        dialogConfig.disableClose = false;
        dialogConfig.id = 'claims-no-devices-dialog';
        dialogConfig.height = 'auto';
        dialogConfig.data = {
            subtitle: "Global_Modal_NoDevices_Subtitle",
            title: "Global_Modal_NoDevices_Title",
            description: "Global_Modal_NoDevices_Description",
            buttonText: "Global_Modal_NoDevices_ButtonText"
        };

        const dialogRef = this.matDialog.open(ClaimsNoDevicesModalWindowComponent, dialogConfig);

        dialogRef.afterClosed().subscribe(res => {
            if (res.data && res.data.result === 'success') {
                this.router.navigate([this.resourceService.getPortalSetting("URL_MyDevices").Value]);
            }
        });
    }

    coverageButtonClick(navigationItem: SitefinityMenuItem) {
        let url = navigationItem.LinkUrl;
        if (navigationItem?.LinkUrl?.startsWith('/')) {
            this.router.navigate([url]);
        }
    }

    viewClaimHistoryAndStatus() {
        const searchClaimsUrl = this.resourceService.getPortalSetting("URL_ClaimsPortal").Value;

        const joined$ = combineLatest(
            [
                this.userSettings.$selectedContract,
            ]
        ).pipe(
            filter(([a]) => a !== null),
            takeUntil(this.destroy$)
        );

        joined$.subscribe(([selectedContract]:
            [ISelectedContract]) => {
            this.isActive = this.contractGuard.activateGuard(selectedContract, false);
            this.isGSSP = selectedContract.useGSSPClaims;
            this.masterCertificateNumber = selectedContract?.id;
            this.childCertificateNumber = selectedContract?.certificateNumberForGadgetsPolicy;
            this.dealerCode = selectedContract.dealerId;
            this.coverageTypeCode = 'AD';
            this.zipCode = selectedContract?.zipCode;
            this.contractNum = selectedContract && selectedContract?.members && selectedContract?.members.length ? selectedContract?.members[0].contractNumber : '';
        });

        if (this.isGSSP) {
            if (this.masterCertificateNumber && this.masterCertificateNumber.length > 0) {
                const childCertificateNumbers: [string] = [this.childCertificateNumber];
                this.gsspAuthService.postGenerateGSSPToken(this.dealerCode, this.masterCertificateNumber, childCertificateNumbers, null).subscribe({
                    next: (gsspAuthToken: any) => {
                        if (gsspAuthToken && gsspAuthToken.length > 0) {
                            this.redirectUrl = `${searchClaimsUrl}?accessToken=${gsspAuthToken}`;
                            this.redirectUrl = this.domSanitizer.sanitize(SecurityContext.URL, this.redirectUrl);
                            window.open(this.redirectUrl, '_self');
                        } else {
                            this.globalModalWindowService.showModalWindowMin('Global_Modal_Error_InvalidGsspAuthToken_Title', 'GetGsspAuthToken');
                        }
                    }
                });
            } else {
                this.globalModalWindowService.showModalWindowMin('Global_Modal_Error_MissingMasterCertificate_Title', 'GetGsspAuthToken');
            }
        } else {
            const tokens = JSON.parse(localStorage.getItem(this.OKTA_TOKEN_STORAGE));
            const accessToken = tokens.access_token && tokens.access_token.accessToken
                ? tokens.access_token.accessToken
                : tokens?.accessToken?.accessToken;
            const hashedQuery = btoa(`cn=${this.contractNum}&zip=${this.zipCode}`);
            if (accessToken.indexOf(' ') >= 0) {
                console.log('invalid token');
            } else {
                this.redirectUrl = `${searchClaimsUrl}?q=${hashedQuery}&token=${accessToken}`;
                this.redirectUrl = this.domSanitizer.sanitize(SecurityContext.URL, this.redirectUrl);
                window.open(this.redirectUrl, '_blank');
            }
        }
    }

    routeToLogin(item) {
        this.location.replaceState(item.LinkUrl);
        this.router.navigate([item.LinkUrl]).then(() => {
            window.location.reload();
        });
    }

    getSelectedLanguage() {
        return this.selectedLanguageKey || 'EN';
    }

    toggleLanguage() {
        const language = this.languages.find(x => x.Key.toLocaleLowerCase() != this.getSelectedLanguage().toLocaleLowerCase());
        this.storageService.setLocal<string>(`language`, language?.Key);
        window.location.reload();
    }

    private async clearSession() {
        this.oktaAuth.tokenManager.clear();
        await this.oktaAuth.closeSession();
        await this.oktaAuth.revokeAccessToken();
    }
}
